import { CheckCircle } from '@untitled-ui/icons-react/build/cjs';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ButtonCmp from 'src/components/ButtonCmp';
import Confirmation from 'src/components/Confirmation';
import { ROUTES } from 'src/constants/routes';
import { PasswordFormData, PasswordErrorMsg, PasswordProps } from 'src/interface/common';
import { setIsAuthenticated } from 'src/redux/auth.slice';
import { resetNewPassword } from 'src/services/AuthService';
import { initFormData, initErrorMsg, validatePassword, validatePasswordMatch } from 'src/utils/generatePassword';

import AuthWrapper from '../AppLayout/AuthWrapper';

import UpdatePasswordForm from './UpdatePasswordForm';

const PasswordManager: React.FC<PasswordProps> = ({ type }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [code, setCode] = useState<string | null>(null);
    const [formData, setFormData] = useState<PasswordFormData>(initFormData);
    const [errorMsg, setErrorMsg] = useState<PasswordErrorMsg>(initErrorMsg);
    const [isStrongPassword, setIsStrongPassword] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [submittedForm, setSubmittedForm] = useState(false);

    const handlePasswordValidation = () => {
        const { isValid, message } = validatePassword(formData.password);

        if (!isValid) {
            setErrorMsg({
                ...errorMsg,
                password: message,
                confirm_password: '',
            });
            setIsStrongPassword(false);

            return false;
        }

        setErrorMsg({ ...errorMsg, password: '' });
        setIsStrongPassword(true);

        return true;
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormData({ ...formData, [name]: value });

        // Clear errors when typing
        setErrorMsg(initErrorMsg);
    };

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        setErrorMsg(initErrorMsg);

        const isPasswordValid = handlePasswordValidation();
        const isPasswordMatch = validatePasswordMatch(formData.password, formData.confirm_password);

        if (!isPasswordValid) {
            return;
        }

        if (!isPasswordMatch) {
            setErrorMsg({
                ...errorMsg,
                confirm_password: t('Auth.Password_Manager.Password_Mismatch'),
            });

            return;
        }

        setIsLoading(true);
        const payload = {
            password: formData.password,
            confirm_password: formData.confirm_password,
            code,
        };

        resetNewPassword(payload)
            .then((response: any) => {
                console.log('response', response);
                setIsLoading(false);

                if (type === 'create') {
                    dispatch(setIsAuthenticated(true));
                    navigate(ROUTES.SETUP_ACCOUNT);
                } else {
                    setSubmittedForm(true);
                }
            })
            .catch((error: any) => {
                setIsLoading(false);
                setErrorMsg({
                    ...initErrorMsg,
                    server_error: error?.response?.message || t('Auth.Password_Manager.Password_Setup_Error'),
                });
            });
    };

    useEffect(() => {
        const codeVal = localStorage.getItem('verify_code'); //new URLSearchParams(location?.search).get('code');

        if (!codeVal) {
            navigate(ROUTES.LOGIN);

            return;
        }
        setCode(codeVal);
    }, []);

    if (submittedForm) {
        return (
            <AuthWrapper>
                <Confirmation
                    title={t('Auth.Password_Manager.Password_Set')}
                    desc={t('Auth.Password_Manager.Password_Reset_Success')}
                    Icon={<CheckCircle className="w-7 h-7" />}
                    handleActionType={() => dispatch(setIsAuthenticated(true))}
                    buttonText={t('Auth.Password_Manager.Go_To_Dashboard')}
                    buttonType="btn_primary_black btn-custom-lg"
                    titleClassName="mb-2 !text-[16px] !leading-[24px] !font-semibold"
                    descClassName="!text-sm !px-8 !py-0 !font-normal sm:!max-w-[360px] !text-grayLight600"
                />
                <ButtonCmp className="text-base font-semibold text-white w-full" onClick={() => dispatch(setIsAuthenticated(true))}>
                    {t('Auth.Password_Manager.Continue')}
                </ButtonCmp>
            </AuthWrapper>
        );
    }

    return (
        <AuthWrapper
            authHeading={type === 'create' ? t('Auth.Password_Manager.Create_Password') : t('Auth.Password_Manager.Set_New_Password')}
            authSubTitle={t('Auth.Password_Manager.Secure_Account')}
        >
            <UpdatePasswordForm formData={formData} errorMsg={errorMsg} isStrongPassword={isStrongPassword} isLoading={isLoading} handleSubmit={handleSubmit} handleInputChange={handleInputChange} />
        </AuthWrapper>
    );
};

export default PasswordManager;
